import { useMemo } from 'react'
import {
  type RegularPlanAlertVariant,
  RegularPlanAlertVariantEnum,
  type SingleFundCommissionsData,
  type SingleFundGraphData,
  type PortfolioAlertData,
  type RegularPlanAlertSheetResponse,
  type FundInfo,
} from './types'
import { VariationSingleFundCommissions } from './components/VariationSingleFundCommissions'
import { VariationSingleFundGraph } from './components/VariationSingleFundGraph'
import { VariationPortfolioAlert } from './components/VariationPortfolioAlert'

function buildFundInfo(sheetData: RegularPlanAlertSheetResponse): FundInfo {
  return {
    name: sheetData?.fundName ?? '',
    category: sheetData?.fundCategory ?? '',
    nature: sheetData?.fundNature ?? '',
    logoUrl: sheetData?.logoUrl ?? '',
  }
}

interface RegularPlanAlertSheetContentProps {
  variant: RegularPlanAlertVariant
  sheetData?: RegularPlanAlertSheetResponse | null
}

export function RegularPlanAlertSheetContent({ variant, sheetData }: RegularPlanAlertSheetContentProps) {
  const fund = useMemo(() => (sheetData ? buildFundInfo(sheetData) : null), [sheetData])

  const singleFundCommissionsData: SingleFundCommissionsData | null = useMemo(() => {
    if (!sheetData || !fund) return null
    return {
      commissionAmount: sheetData?.commissions ?? 0,
      fund: fund ?? null,
    }
  }, [sheetData, fund])

  const singleFundGraphData: SingleFundGraphData | null = useMemo(() => {
    if (!sheetData || !fund) return null
    return {
      commissionAmount: sheetData?.commissions ?? 0,
      regularReturns: sheetData?.regularReturns ?? 0,
      directReturns: sheetData?.directReturns ?? 0,
      fund: fund ?? null,
    }
  }, [sheetData, fund])

  if (
    (variant === RegularPlanAlertVariantEnum.REGULAR_FUND_COMMISSIONS_BOTTOM_SHEET && !singleFundCommissionsData) ||
    (variant === RegularPlanAlertVariantEnum.REGULAR_FUND_COMMISSIONS_GRAPH_BOTTOM_SHEET && !singleFundGraphData)
  ) {
    return (
      <div className="font-[400] text-center text-[16px] text-[#992617] py-8">
        Data unavailable.
      </div>
    )
  }

  switch (variant) {
    case RegularPlanAlertVariantEnum.REGULAR_FUND_COMMISSIONS_BOTTOM_SHEET:
      return (
        singleFundCommissionsData && (
          <VariationSingleFundCommissions data={singleFundCommissionsData} />
        )
      )
    case RegularPlanAlertVariantEnum.REGULAR_FUND_COMMISSIONS_GRAPH_BOTTOM_SHEET:
      return (
        singleFundGraphData && (
          <VariationSingleFundGraph data={singleFundGraphData} />
        )
      )
    case RegularPlanAlertVariantEnum.REGULAR_FUND_COMMISSIONS_SUMMARY_BOTTOM_SHEET:
    default: {
      const portfolioData: PortfolioAlertData = sheetData
        ? { totalCommissionsPaid: sheetData?.commissions ?? 0, totalRegularFunds: sheetData?.totalRegularFundCount ?? 0 }
        : { totalCommissionsPaid: 0, totalRegularFunds: 0 }
      return <VariationPortfolioAlert data={portfolioData} />
    }
  }
}
